import type { WorkspaceView } from "./types";

/**
 * Renders a workspace reference as a single human-friendly string.
 *
 * Rules:
 *   - Resolved view, no friendly name → "Desk 12 · Desk (d508a2ed-1535-…)"
 *   - Resolved view, with friendly name → "Room 332 (I.T. Collaboration Room) · Room (d508a2ed-…)"
 *   - Unresolved (pending lookup, 404) → just the id
 *
 * The id is always shown alongside the friendly label so admins still
 * have the canonical reference to copy/paste into other tools when
 * troubleshooting an assignment, and so the display is unambiguous
 * across workspaces with the same name (e.g. "Desk 12" in two offices).
 *
 * Friendly name (when present) is wrapped in parentheses immediately
 * after `name` so it reads naturally — `Room 332 (I.T. Collaboration
 * Room)` — rather than competing with the `· Type` separator that
 * already segments the rest of the label.
 */
export function formatWorkspaceDisplay(
    workspace: WorkspaceView | null | undefined,
    fallbackId: string,
): string {
    if (workspace === null || workspace === undefined) {
        return fallbackId;
    }
    const friendly =
        workspace.friendlyName !== null && workspace.friendlyName !== undefined && workspace.friendlyName !== ""
            ? ` (${workspace.friendlyName})`
            : "";
    return `${workspace.name}${friendly} \u00b7 ${workspace.type} (${workspace.id})`;
}
